/**
 * src/data/trouble-keywords.js
 *
 * Shared trouble-keyword list and matcher used by:
 *  - Phase 22 sick-plant upgrade trigger (ONB-03 D-11)
 *  - Phase 23 trouble-keyword affiliate product trigger (ONB-05)
 *
 * D-11 specifies 10 core keywords; 'black spot' is added as a phrase
 * variant of 'blackspot' to handle spaced spellings without false positives.
 *
 * Match modes:
 *  - 'word'   → case-insensitive whole-word match (\b{word}\b)
 *  - 'any'    → case-insensitive substring match
 *  - 'phrase' → case-insensitive substring match for multi-word phrases
 */

// Iteration order matters: matchesTroubleKeyword returns the FIRST match.
// Multi-word phrases and more-specific tokens appear before their generic
// substrings (e.g. 'powdery mildew' before 'mildew', 'red spider' before 'mite').
export const TROUBLE_KEYWORDS = Object.freeze([
  // Multi-word phrases (matched first so they win over their substrings)
  { word: 'powdery mildew',  match: 'phrase' },
  { word: 'red spider mite', match: 'phrase' },
  { word: 'spider mite',     match: 'phrase' },
  { word: 'vine weevil',     match: 'phrase' },
  { word: 'leaf spot',       match: 'phrase' },
  { word: 'holes in leaves', match: 'phrase' },
  { word: 'black spot',      match: 'phrase' },

  // Original D-11 set
  { word: 'yellowing',  match: 'any'    },
  { word: 'wilting',    match: 'any'    },
  { word: 'aphids',     match: 'word'   },
  { word: 'blackspot',  match: 'any'    },
  { word: 'mildew',     match: 'any'    },
  { word: 'fungus',     match: 'any'    },
  { word: 'pest',       match: 'word'   },
  { word: 'dying',      match: 'word'   },
  { word: 'rust',       match: 'word'   },
  { word: 'scale',      match: 'word'   },

  // Phase 23 v2 — comprehensive pest and disease coverage
  // Pests
  { word: 'slugs',       match: 'word' },
  { word: 'slug',        match: 'word' },
  { word: 'snails',      match: 'word' },
  { word: 'snail',       match: 'word' },
  { word: 'caterpillar', match: 'any'  }, // catches caterpillars
  { word: 'whitefly',    match: 'any'  }, // catches whiteflies
  { word: 'greenfly',    match: 'any'  },
  { word: 'blackfly',    match: 'any'  },
  { word: 'mealybug',    match: 'any'  }, // catches mealybugs
  { word: 'weevil',      match: 'word' }, // fallback after 'vine weevil'
  { word: 'thrip',       match: 'any'  }, // catches thrips
  { word: 'moth',        match: 'word' },
  { word: 'maggot',      match: 'any'  }, // catches maggots
  { word: 'grub',        match: 'word' }, // vine weevil grubs, chafer grubs

  // Diseases
  { word: 'blight', match: 'word' },
  { word: 'mould',  match: 'word' },
  { word: 'mold',   match: 'word' },
  { word: 'canker', match: 'word' },

  // Visible symptoms (what a gardener actually types)
  { word: 'eaten',   match: 'word' },
  { word: 'chewed',  match: 'word' },
  { word: 'sticky',  match: 'word' },
  { word: 'sooty',   match: 'word' },
  { word: 'bugs',    match: 'word' },
  { word: 'insects', match: 'word' },
]);

/**
 * Test whether a note string contains any trouble keyword.
 *
 * @param {string|null} text - The note text to test
 * @returns {string|null} The matched keyword string, or null if no match
 */
export function matchesTroubleKeyword(text) {
  if (!text || typeof text !== 'string') return null;
  const lower = text.toLowerCase();
  for (const { word, match } of TROUBLE_KEYWORDS) {
    if (match === 'word') {
      // Whole-word match — prevents 'pest' matching 'pestered', 'rust' matching 'rusty', etc.
      const re = new RegExp(`\\b${word}\\b`, 'i');
      if (re.test(text)) return word;
    } else {
      // Substring match — covers 'any' and 'phrase' modes
      if (lower.includes(word.toLowerCase())) return word;
    }
  }
  return null;
}
